import { inject } from "@angular/core";
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from "@angular/router";

import { EnvironmentLoaderService } from "src/ancestors/env-config.service";
import { UrlCollection } from "../app-routing.module";
import { TokenManagerService } from "./token-manager.service";
import { TokenStorageService } from "./token-storage.service";
import { BaseLoginInfo } from "./user.service";


/**
 * Fa il redirect all'url di login, in base al tipo di login
 * aggiungendo anche l' originalRequestedUrl per tornare all'url originale dopo il login
 *
 * @param {string} urlSnapshot - alla qualle si tenta di accedere.
 */
async function redirectToLoginUrlFn(urlSnapshot: string): Promise<void> {

  const envConfig = inject(EnvironmentLoaderService);
  const env = envConfig.getEnvConfig();
  const router = inject(Router);

  if (env.loginType == "local"
    // Se si sta accedendo alla pagina di localLogin, anche nel caso in cui sia definito il login sso, bypasso il redirect di default che
    // altrimenti porterebbe l'utente a dover accedere tramite sso a causa dell'authentication guard per via del redirect del path "/"
    || window.location.href.endsWith(`/#/${UrlCollection.localAuthentication}`)) {
    await router.navigate([env.loginUrl], {
      queryParams: {
        originalRequestedUrl: urlSnapshot
      }
    });
  } else {
    await router.navigate([env.loginUrl], {
      queryParams: {
        originalRequestedUrl: urlSnapshot
      }
    });
  }

}

/**
 * Implementa una guard di navigazione per verificare l'autenticazione utente.
 *
 * @param {ActivatedRouteSnapshot} route - Lo stato dell'attuale route attivato.
 * @param {RouterStateSnapshot} state - Lo stato corrente del router.
 * @returns {Promise<boolean>} Una Promise che restituisce true se l'utente è autenticato, altrimenti false.
 */
export async function AuthenticationGuardFn(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
  const envConfig = inject(EnvironmentLoaderService);
  const tkManager = inject(TokenManagerService);
  const tStorage = inject(TokenStorageService);
  const env = envConfig.getEnvConfig();


  const tokenKey = env.loginType === "local" ? env.localTokenKey : env.ssoTokenKey;

  // NON esiste (e non deve esistere) un accesso a partire da un token in query string.
  // Qui c'era un ramo che, trovando un parametro `ssortkqp`, lo scambiava per una sessione
  // completa: era codice morto, perché la condizione che lo proteggeva controllava il NOME della
  // chiave del token (una costante, quindi mai "invalido") invece del token. Non era però innocuo:
  // gli endpoint che chiamava — `authn/decodeSingleUseToken` e `authn/createSingleUseToken` — non
  // esistono nel backend di questo prodotto, e `loginType` è `local` in ogni ambiente. Correggere
  // la condizione senza accorgersene avrebbe ATTIVATO un login via URL, con il token negli access
  // log del server, nella cronologia del browser e nell'header `Referer`.
  // La sessione la decide il token, e la valuta `TokenManagerService.checkTokenValidity`.
  const token: BaseLoginInfo = tStorage.retriveToken(tokenKey);
  const tokenIsValid: boolean = tkManager.checkTokenValidity(token);

  if (token?.jwtPayload && !tokenIsValid) {
    // prova a fare refresh del token
    try {
      const newToken = await tkManager.refreshToken(token);
      if (newToken) {
        tStorage.saveToken(tokenKey, newToken);
        return true;
      }
    } catch (error) {
      // rimuovi il token
      tStorage.deleteToken(tokenKey);
      return false;
    }
  }

  /**
   * Se esiste un token di autenticazione valido verrà permesso di eseguire l'accesso
   */
  if (token && tokenIsValid) {
    return true;
  }

  // Qui c'era uno `switch (env.storeAccessToken)` i cui due rami differivano per la sola riga
  // seguente: la modalità a cookie NON cancellava la sessione scaduta prima di reindirizzare.
  // Con una terza modalità di storage quel ramo sarebbe diventato una trappola (si comporta bene,
  // ma per caso, cadendo nel `default`), e la differenza non aveva una ragione: una sessione non
  // più valida va rimossa comunque, altrimenti resta nello storage a far ripartire lo stesso giro.
  tStorage.deleteToken(tokenKey);

  await redirectToLoginUrlFn(state.url);

  return false;

}